// Report numbers (GIA digits, IGI "LG" + digits) are upper case in the diamond inventory; stock numbers
// (igi-30cbd4e1, 1875411bc) are lower case. The inventory lookup is case-sensitive.
const DIAMOND_REPORT_PATTERN = /^(LG)?\d{6,12}$/i;
const DIAMOND_STOCK_PATTERN = /^(igi-)?[0-9a-f]{6,}$/i;

function cleanDiamondLookupTerm(term) {
  return String(term || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/^#/, "");
}

function isDiamondLookupTerm(term) {
  const value = cleanDiamondLookupTerm(term);
  return DIAMOND_REPORT_PATTERN.test(value) || DIAMOND_STOCK_PATTERN.test(value);
}

function normaliseDiamondLookupTerm(term) {
  const value = cleanDiamondLookupTerm(term);
  if (DIAMOND_REPORT_PATTERN.test(value)) return value.toUpperCase();
  if (DIAMOND_STOCK_PATTERN.test(value)) return value.toLowerCase();
  return value;
}

// Diamond prices arrive in cents; shown in whole units.
function formatDiamondPrice(cents) {
  const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
  const value = (parseFloat(cents) * rate) / 100;
  if (!isFinite(value) || value <= 0) return "";
  const currency = window.Shopify?.currency?.active || "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(value));
  } catch (error) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(value));
  }
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(searchTerm);
    // The diamond inventory is only queried for report numbers (GIA digits, IGI "LG" + digits) and stock numbers,
    // not for every word typed.
    if (isDiamondLookupTerm(searchTerm)) {
      this.getSearchDiamondsResults(normaliseDiamondLookupTerm(searchTerm));
    } else {
      this.renderDiamondsSearchResults([]);
    }
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchDiamondsResults(searchTerm) {
    const query = window.diamondQueryTemplate;
    const variables = {
      filter: {
        sku: searchTerm,
      },
    };
    fetch(`${window.apiBaseUrl}/graphql`, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: query,
        variables: variables,
      }),
    })
      .then((response) => response.json())
      .then((result) => {
        // Ignore a late answer for a term the shopper has already changed.
        if (normaliseDiamondLookupTerm(this.getQuery()) !== searchTerm) return;
        const items = result?.data?.diamondInventorySearch?.items;
        this.renderDiamondsSearchResults(items);
      })
      .catch((error) => {
        console.error("Error fetching diamond search results:", error);
      });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product&${encodeURIComponent("resources[limit]")}=4&section_id=predictive-search`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  renderDiamondsSearchResults(results) {
    const diamondsResultsContainer = document.getElementById(
      "predictive-search-results",
    );
    if (!diamondsResultsContainer) return;

    const existingContainer = diamondsResultsContainer.querySelector(
      ".predictive-diamonds-results",
    );
    if (!results || results.length === 0) {
      if (existingContainer) existingContainer.innerHTML = "";
      return;
    }

    //Create an inner container if it doesn't exist
    if (existingContainer === null) {
      const innerContainer = document.createElement("div");
      innerContainer.classList.add("predictive-diamonds-results");
      diamondsResultsContainer.prepend(innerContainer);
    }

    //Remove any previous results
    const innerContainer = diamondsResultsContainer.querySelector(
      ".predictive-diamonds-results",
    );
    innerContainer.innerHTML = "";

    //Loop throught the results and create the markup for each diamond
    results.forEach((diamond, index) => {
      const diamondElement = document.createElement("div");
      diamondElement.classList.add("diamond-result");
      const diamondName = `${parseFloat(diamond.carat).toFixed(2)} Carat ${diamond.shape} ${diamond.is_labgrown == 1 ? "Lab-grown" : "Natural"} Diamond`,
        diamondUrl =
          `/apps/diamondsearch/${diamond.carat}-carat-${diamond.shape}-diamond-${diamond.sku}`
            .replaceAll(".", "-")
            .toLocaleLowerCase();

      if (index === 0) {
        const heading = document.createElement("div");
        heading.setAttribute(
          "style",
          "margin-bottom: 10px; text-transform: uppercase; font-size: 1.4rem;",
        );
        heading.textContent = "Diamond Search";
        diamondElement.appendChild(heading);
      }

      const link = document.createElement("a");
      link.href = diamondUrl;
      link.className = "diamond-link";

      const imageWrapper = document.createElement("div");
      imageWrapper.className = "diamond-image";
      const image = document.createElement("img");
      image.src = diamond.diamond_image || "";
      image.alt = diamondName;
      image.width = 54;
      image.height = 54;
      imageWrapper.appendChild(image);

      const info = document.createElement("div");
      info.className = "diamond-info";
      const name = document.createElement("h3");
      name.className = "diamond-name";
      name.textContent = diamondName;
      const price = document.createElement("div");
      price.className = "diamond-price";
      price.textContent = formatDiamondPrice(diamond.price) || "Price on request";
      info.appendChild(name);
      info.appendChild(price);

      link.appendChild(imageWrapper);
      link.appendChild(info);
      diamondElement.appendChild(link);
      innerContainer.appendChild(diamondElement);
    });
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.getElementById("shopify-section-header").getBoundingClientRect()
        .bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
